<div class="px-2">
  <button
    type="button"
    class="btn btn-link text-primary ps-0 d-inline-flex align-items-center gap-1 mb-2 text-decoration-none"
    (click)="goBack()"
    >
    <i-bs name="chevron-left" width="16" height="16"></i-bs>
    Back
  </button>

  @if (loading && !user) {
    <div class="text-center py-5 text-muted">
      <span class="spinner-border spinner-border-sm me-2" role="status"></span>
      Loading…
    </div>
  }

  @if (errorMessage && !user) {
    <div class="card border bg-light rounded-3 shadow-none">
      <div class="card-body text-center py-4 text-muted">
        <i-bs name="exclamation-triangle-fill" width="2rem" height="2rem" class="d-block mx-auto mb-2 text-danger"></i-bs>
        {{ errorMessage }}
      </div>
    </div>
  }

  @if (user && pair) {
    <div class="bg-white rounded-3 p-3">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div class="d-flex align-items-center gap-3">
          <span
            class="rounded-circle bg-primary-dark text-white fw-bold d-flex align-items-center justify-content-center flex-shrink-0"
            style="width: 44px; height: 44px; font-size: 16px"
            >
            {{ (user?.demographic?.firstName?.[0] ?? '') + (user?.demographic?.lastName?.[0] ?? '') }}
          </span>
          <div class="d-flex flex-column">
            <div class="d-flex align-items-center gap-2">
              <span class="fw-semibold fs-6 text-gray-dark">{{ user?.demographic?.firstName }} {{ user?.demographic?.lastName }}</span>
              <span class="badge rounded-pill bg-opacity-25 bg-success-light text-success">{{ user?.status }}</span>
            </div>
            <div class="d-flex flex-column small text-muted">
              <span>{{ user?.demographic?.email }}</span>
              <span>{{ user?.demographic?.phone | formatPhoneNumber }}</span>
            </div>
          </div>
        </div>
        <span class="small text-muted text-break">{{ pairTitle }}</span>
      </div>

      <div class="live-map-row d-flex gap-3 flex-wrap flex-lg-nowrap align-items-start">
        <div class="live-map-column flex-grow-1" style="min-width: 0">
          <div class="live-map-card position-relative rounded-3 overflow-hidden">
            @if (!errorMessage && mapParties.length) {
              <app-google-map
                [height]="isExpanded ? 'calc(100vh - 220px)' : '580px'"
                [parties]="mapParties"
                [zones]="zoneConfig"
                [mapStyles]="isDarkMode ? darkMapStyles : lightMapStyles"
                [cleanChrome]="true"
                [zoom]="13"
                [fitPaddingPx]="48"
              ></app-google-map>

              <div class="live-map-caption d-flex flex-column">
                <span class="d-inline-flex align-items-center gap-1">
                  <i-bs name="shield-lock-fill" width="14" height="14"></i-bs>
                  AEGIS PROXIMITY
                </span>
                <span class="live-map-caption-sub">Threat proximity monitor</span>
              </div>

              <span class="live-map-status-pill badge rounded-pill" [ngClass]="statusPillClass">{{ statusPillLabel }}</span>

              @if (isInAlertZone) {
                <div class="live-map-banner-wrap">
                  <div class="live-map-banner d-inline-flex align-items-center gap-2" [ngClass]="zoneState === 'red' ? 'live-map-banner-red' : 'live-map-banner-yellow'">
                    <span class="live-map-banner-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                      <i-bs name="exclamation-triangle-fill" width="13" height="13"></i-bs>
                    </span>
                    <div class="d-flex flex-column">
                      <span class="small fw-semibold">{{ mapBannerTitle }}</span>
                      <span class="small live-map-banner-detail">{{ mapBannerDetail }}</span>
                    </div>
                  </div>
                </div>
              }

              <div class="live-map-telemetry">
                <div class="telemetry-label">Live Telemetry</div>
                <div class="telemetry-sublabel">Current distance</div>
                <div
                  class="telemetry-distance"
                  [ngClass]="zoneState === 'red' ? 'text-danger' : zoneState === 'yellow' ? 'text-warning' : 'text-success'"
                  >{{ distanceDisplayLabel }}</div>

                <div class="telemetry-row"><span>Yellow radius</span><span>{{ zoneRadiiLabel.yellow }}</span></div>
                <div class="telemetry-row"><span>Red radius</span><span>{{ zoneRadiiLabel.red }}</span></div>
                <div class="telemetry-row">
                  <span>Status</span>
                  <span [ngClass]="zoneState === 'red' ? 'text-danger' : zoneState === 'yellow' ? 'text-warning' : 'text-success'">{{ statusPillLabel }}</span>
                </div>
                <div class="telemetry-row"><span>Perp. heading</span><span>{{ perpetratorSummary?.headingLabel ?? '—' }}</span></div>
                <div class="telemetry-row"><span>Movement</span><span [ngClass]="proximityTrendClass">{{ proximityTrendLabel() }}</span></div>
                <div class="telemetry-row"><span>Closing rate</span><span [ngClass]="proximityTrendClass">{{ closingRateLabel }}</span></div>
                <div class="telemetry-row"><span>Last update</span><span>{{ perpetratorSummary?.lastUpdatedLabel ?? '—' }}</span></div>
                <div class="telemetry-row"><span>Accuracy</span><span>{{ accuracyLabel }}</span></div>
              </div>

              <button
                type="button"
                class="live-map-theme-btn"
                (click)="toggleDarkMode()"
                [attr.aria-label]="isDarkMode ? 'Switch to light map' : 'Switch to dark map'"
                >
                <i-bs [name]="isDarkMode ? 'sun-fill' : 'moon-fill'" width="16" height="16"></i-bs>
              </button>

              <button
                type="button"
                class="live-map-expand-btn"
                (click)="toggleExpanded()"
                [attr.aria-label]="isExpanded ? 'Collapse map' : 'Expand map'"
                >
                <i-bs [name]="isExpanded ? 'arrows-angle-contract' : 'arrows-angle-expand'" width="16" height="16"></i-bs>
              </button>
            } @else if (loading) {
              <div class="text-center py-5 text-white-50">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Loading live locations…
              </div>
            } @else if (errorMessage) {
              <div class="text-center py-5 text-white-50">{{ errorMessage }}</div>
            }
          </div>

          @if (isZoneCentreStale) {
            <div class="small text-warning-emphasis d-flex align-items-center gap-1 mt-2">
              <i-bs name="exclamation-triangle-fill"></i-bs>
              Protected Party's position may be outdated — the zone shown may be unreliable.
            </div>
          }

          <div class="live-map-activity small">
            @if (eventLog.length) {
              @for (line of eventLog; track $index) {
                <div>{{ line }}</div>
              }
            } @else {
              <span class="text-muted">No zone events yet this session.</span>
            }
          </div>
        </div>

        @if (!isExpanded) {
        <div class="live-map-side-card card border-0 rounded-3 shadow-sm flex-shrink-0">
          <div class="card-body d-flex flex-column gap-2 p-2">
            <span class="fw-medium text-gray-dark">{{ counterpartRole }}</span>
            <hr class="my-0" />
            <div class="d-flex align-items-start gap-2">
              <span
                class="rounded-circle bg-primary-dark text-white fw-bold d-flex align-items-center justify-content-center flex-shrink-0"
                style="width: 32px; height: 32px; font-size: 12px"
                >{{ counterpartUser?.initials }}</span>
              <div class="d-flex flex-column flex-grow-1">
                <span class="fw-medium text-gray-dark">{{ counterpartUser?.name }}</span>
                @if (counterpartUser?.organization?.name) {
                  <span class="badge rounded-pill bg-opacity-10 bg-info text-info align-self-start">{{ counterpartUser?.organization?.name }}</span>
                }
                <div class="d-flex gap-2 small text-muted mt-1 align-items-center">
                  <span class="d-inline-flex align-items-center gap-1">
                    <i class="fas fa-map-marker-alt"></i>
                    {{ pair.distanceThreshold || 0 }} m
                  </span>
                  <span class="d-inline-flex align-items-center gap-1">
                    <span
                      class="rounded-circle flex-shrink-0 d-inline-block"
                      [ngClass]="pair.status.toLowerCase() === 'active' ? 'bg-success' : 'bg-secondary'"
                      style="width: 6px; height: 6px"
                      ></span>
                    {{ pair.status | titlecase }}
                  </span>
                </div>
              </div>
            </div>
            @if (isInAlertZone) {
              <button type="button" class="live-map-alert-btn btn btn-sm rounded-1 d-inline-flex align-items-center gap-1 align-self-start">
                <i-bs name="exclamation-triangle-fill" width="14" height="14"></i-bs>
                Alert
              </button>
            }
          </div>
        </div>
        }
      </div>
    </div>
  }
</div>
